import React, { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";

import { apiRequest } from "@mhj/shared/api";
import { LAPAI_WARDS } from "@mhj/shared/wards";
import { useAuth } from "@mhj/shared/auth";

type Cycle = {
  id: string;
  name: string;
  code: string;
  year: number;
  status: "draft" | "open" | "closed" | "archived";
  opensAt: string | null;
  closesAt: string | null;
  allowedWards: string[] | null;
  blockPriorBeneficiaries: boolean;
  resultsPublished: boolean;
  lastSequence: number;
};

type Track = {
  id: string;
  categoryId: string;
  name: string;
  active: boolean | null;
};

type CycleTrack = { id: string; cycleId: string; trackId: string; slots: number; open: boolean | null };

const STATUS_STYLES: Record<Cycle["status"], string> = {
  draft: "bg-slate-100 text-slate-600",
  open: "bg-emerald-100 text-emerald-700",
  closed: "bg-amber-100 text-amber-700",
  archived: "bg-slate-100 text-slate-400",
};

/** What a cycle in this state is allowed to become next. */
const NEXT_STATUS: Record<Cycle["status"], Cycle["status"][]> = {
  draft: ["open"],
  open: ["closed"],
  closed: ["archived", "open"],
  archived: [],
};

export default function AdminEmpowermentCycles() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const isSuperAdmin = user?.role === "super_admin";

  const [showForm, setShowForm] = useState(false);
  const [selectedCycle, setSelectedCycle] = useState<string | null>(null);
  const [form, setForm] = useState({
    name: "",
    code: "",
    year: new Date().getFullYear(),
    opensAt: "",
    closesAt: "",
    allowedWards: [] as string[],
  });

  const { data, error, isLoading } = useQuery({
    queryKey: ["empowerment", "cycles"],
    queryFn: () => apiRequest<{ cycles: Cycle[] }>("/admin/empowerment/cycles"),
  });

  const { data: trackData } = useQuery({
    queryKey: ["empowerment", "tracks"],
    queryFn: () => apiRequest<{ tracks: Track[] }>("/admin/empowerment/tracks"),
  });

  const { data: cycleTrackData } = useQuery({
    queryKey: ["empowerment", "cycle-tracks", selectedCycle],
    enabled: !!selectedCycle,
    queryFn: () =>
      apiRequest<{ cycleTracks: CycleTrack[] }>(
        `/admin/empowerment/cycles/${selectedCycle}/tracks`,
      ),
  });

  useEffect(() => {
    if (error) toast.error((error as Error).message);
  }, [error]);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ["empowerment"] });
  };

  const createCycle = useMutation({
    mutationFn: () =>
      apiRequest<{ cycle: Cycle }>("/admin/empowerment/cycles", {
        method: "POST",
        withCsrf: true,
        data: {
          name: form.name,
          code: form.code,
          year: Number(form.year),
          opensAt: form.opensAt ? new Date(form.opensAt).toISOString() : null,
          closesAt: form.closesAt ? new Date(form.closesAt).toISOString() : null,
          // An empty selection means every ward, which is the common case.
          allowedWards: form.allowedWards.length ? form.allowedWards : null,
        },
      }),
    onSuccess: () => {
      toast.success("Cycle created");
      setShowForm(false);
      setForm({ name: "", code: "", year: new Date().getFullYear(), opensAt: "", closesAt: "", allowedWards: [] });
      invalidate();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const changeStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: Cycle["status"] }) =>
      apiRequest(`/admin/empowerment/cycles/${id}/status`, {
        method: "POST",
        withCsrf: true,
        data: { status },
      }),
    onSuccess: () => {
      toast.success("Cycle updated");
      invalidate();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const deleteCycle = useMutation({
    mutationFn: (id: string) =>
      apiRequest(`/admin/empowerment/cycles/${id}`, { method: "DELETE", withCsrf: true }),
    onSuccess: () => {
      toast.success("Cycle deleted");
      invalidate();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const saveTracks = useMutation({
    mutationFn: (tracks: { trackId: string; slots: number }[]) =>
      apiRequest(`/admin/empowerment/cycles/${selectedCycle}/tracks`, {
        method: "PUT",
        withCsrf: true,
        data: { tracks },
      }),
    onSuccess: () => {
      toast.success("Tracks updated");
      invalidate();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const cycles = data?.cycles ?? [];
  const tracks = trackData?.tracks ?? [];
  const cycleTracks = cycleTrackData?.cycleTracks ?? [];
  const slotsFor = (trackId: string) =>
    cycleTracks.find((ct) => ct.trackId === trackId)?.slots ?? 0;

  const toggleWard = (ward: string) =>
    setForm((f) => ({
      ...f,
      allowedWards: f.allowedWards.includes(ward)
        ? f.allowedWards.filter((w) => w !== ward)
        : [...f.allowedWards, ward],
    }));

  return (
    <div className="space-y-6">
      <div className="glass-card rounded-3xl p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-blue-500">
              Empowerment
            </p>
            <h2 className="mt-2 text-2xl font-semibold text-slate-900">
              Cycles &amp; tracks
            </h2>
            <p className="mt-2 max-w-2xl text-sm text-slate-600">
              A cycle is one round of the programme, with its own dates, wards and
              places. Only one cycle can be open at a time.
            </p>
          </div>
          {isSuperAdmin && (
            <button
              type="button"
              onClick={() => setShowForm((s) => !s)}
              className="rounded-full bg-blue-600 px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] text-white transition hover:bg-blue-500"
            >
              {showForm ? "Cancel" : "New cycle"}
            </button>
          )}
        </div>

        {!isSuperAdmin && (
          <p className="mt-4 rounded-2xl bg-amber-50 px-4 py-3 text-sm text-amber-800">
            You can view cycles. Creating and changing them is restricted to a
            super admin.
          </p>
        )}
      </div>

      {showForm && isSuperAdmin && (
        <form
          className="glass-card space-y-4 rounded-3xl p-6"
          onSubmit={(e) => {
            e.preventDefault();
            createCycle.mutate();
          }}
        >
          <div className="grid gap-4 sm:grid-cols-3">
            <label className="flex flex-col gap-1 text-sm sm:col-span-2">
              <span className="text-xs uppercase tracking-[0.2em] text-slate-500">Name</span>
              <input
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="2026 First Cycle"
                className="rounded-2xl border border-sky-200 px-4 py-2.5 outline-none focus:ring-2 focus:ring-blue-200"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-xs uppercase tracking-[0.2em] text-slate-500">Year</span>
              <input
                required
                type="number"
                value={form.year}
                onChange={(e) => setForm({ ...form, year: Number(e.target.value) })}
                className="rounded-2xl border border-sky-200 px-4 py-2.5 outline-none focus:ring-2 focus:ring-blue-200"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-xs uppercase tracking-[0.2em] text-slate-500">Code</span>
              <input
                required
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
                placeholder="2026-C1"
                className="rounded-2xl border border-sky-200 px-4 py-2.5 outline-none focus:ring-2 focus:ring-blue-200"
              />
              <span className="text-xs text-slate-400">Used in application numbers</span>
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-xs uppercase tracking-[0.2em] text-slate-500">Opens</span>
              <input
                type="datetime-local"
                value={form.opensAt}
                onChange={(e) => setForm({ ...form, opensAt: e.target.value })}
                className="rounded-2xl border border-sky-200 px-4 py-2.5 outline-none focus:ring-2 focus:ring-blue-200"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-xs uppercase tracking-[0.2em] text-slate-500">Closes</span>
              <input
                type="datetime-local"
                value={form.closesAt}
                onChange={(e) => setForm({ ...form, closesAt: e.target.value })}
                className="rounded-2xl border border-sky-200 px-4 py-2.5 outline-none focus:ring-2 focus:ring-blue-200"
              />
            </label>
          </div>

          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500">
              Wards ({form.allowedWards.length === 0 ? "all ten" : `${form.allowedWards.length} selected`})
            </p>
            <p className="mb-2 text-xs text-slate-400">
              Leave everything unselected to open the cycle to all of Lapai.
            </p>
            <div className="flex flex-wrap gap-2">
              {LAPAI_WARDS.map((ward) => {
                const on = form.allowedWards.includes(ward);
                return (
                  <button
                    key={ward}
                    type="button"
                    onClick={() => toggleWard(ward)}
                    className={`rounded-full border px-3 py-1.5 text-xs transition ${
                      on
                        ? "border-blue-500 bg-blue-50 text-blue-700"
                        : "border-slate-200 text-slate-600 hover:border-blue-300"
                    }`}
                  >
                    {ward}
                  </button>
                );
              })}
            </div>
          </div>

          <button
            type="submit"
            disabled={createCycle.isPending}
            className="rounded-full bg-blue-600 px-6 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] text-white transition hover:bg-blue-500 disabled:opacity-60"
          >
            {createCycle.isPending ? "Creating…" : "Create cycle"}
          </button>
        </form>
      )}

      <div className="glass-card rounded-3xl p-6">
        {isLoading ? (
          <p className="text-sm text-slate-500">Loading cycles…</p>
        ) : cycles.length === 0 ? (
          <p className="text-sm text-slate-500">
            No cycles yet. Create one to start accepting applications.
          </p>
        ) : (
          <div className="space-y-3">
            {cycles.map((cycle) => (
              <div key={cycle.id} className="rounded-2xl border border-slate-200 p-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-3">
                      <h3 className="font-semibold text-slate-900">{cycle.name}</h3>
                      <span
                        className={`rounded-full px-2.5 py-0.5 text-xs font-medium uppercase tracking-wide ${STATUS_STYLES[cycle.status]}`}
                      >
                        {cycle.status}
                      </span>
                    </div>
                    <p className="mt-1 text-xs text-slate-500">
                      {cycle.code} · {cycle.allowedWards?.length
                        ? `${cycle.allowedWards.length} of 10 wards`
                        : "all Lapai wards"}
                      {cycle.lastSequence > 0 && ` · ${cycle.lastSequence} applications`}
                    </p>
                  </div>
                  {isSuperAdmin && (
                    <div className="flex flex-wrap gap-2">
                      {NEXT_STATUS[cycle.status].map((next) => (
                        <button
                          key={next}
                          type="button"
                          onClick={() => changeStatus.mutate({ id: cycle.id, status: next })}
                          className="rounded-full border border-blue-200 px-3 py-1.5 text-xs uppercase tracking-wide text-blue-700 transition hover:bg-blue-50"
                        >
                          {next === "open" ? "Open" : next === "closed" ? "Close" : "Archive"}
                        </button>
                      ))}
                      <button
                        type="button"
                        onClick={() =>
                          setSelectedCycle(selectedCycle === cycle.id ? null : cycle.id)
                        }
                        className="rounded-full border border-slate-200 px-3 py-1.5 text-xs uppercase tracking-wide text-slate-600 transition hover:bg-slate-50"
                      >
                        Tracks
                      </button>
                      {cycle.status === "draft" && (
                        <button
                          type="button"
                          onClick={() => {
                            if (confirm(`Delete ${cycle.name}? This cannot be undone.`)) {
                              deleteCycle.mutate(cycle.id);
                            }
                          }}
                          className="rounded-full border border-red-200 px-3 py-1.5 text-xs uppercase tracking-wide text-red-600 transition hover:bg-red-50"
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {selectedCycle === cycle.id && (
                  <div className="mt-4 border-t border-slate-100 pt-4">
                    <p className="mb-1 text-xs uppercase tracking-[0.2em] text-slate-500">
                      Tracks and places
                    </p>
                    <p className="mb-3 text-xs text-slate-400">
                      Places are a planning target shown to reviewers. Applications
                      continue past them.
                    </p>
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        const data = new FormData(e.currentTarget);
                        const entries = tracks
                          .map((t) => ({
                            trackId: t.id,
                            slots: Number(data.get(`slots-${t.id}`) ?? 0),
                          }))
                          .filter((entry) => entry.slots > 0);
                        saveTracks.mutate(entries);
                      }}
                    >
                      <div className="grid gap-2 sm:grid-cols-2">
                        {tracks.map((track) => (
                          <label
                            key={track.id}
                            className="flex items-center justify-between gap-3 rounded-xl bg-slate-50 px-3 py-2 text-sm"
                          >
                            <span className="text-slate-700">{track.name}</span>
                            <input
                              type="number"
                              min={0}
                              name={`slots-${track.id}`}
                              defaultValue={slotsFor(track.id)}
                              className="w-20 rounded-lg border border-slate-200 px-2 py-1 text-right"
                            />
                          </label>
                        ))}
                      </div>
                      <button
                        type="submit"
                        disabled={saveTracks.isPending}
                        className="mt-3 rounded-full bg-blue-600 px-5 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-white transition hover:bg-blue-500 disabled:opacity-60"
                      >
                        {saveTracks.isPending ? "Saving…" : "Save places"}
                      </button>
                    </form>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
